import { Minus, Plus, X } from 'lucide-preact'
import { useLayoutEffect, useRef } from 'preact/hooks'
import { text } from '#contract'
import { useT } from '../i18n/index.ts'
import styles from './app-frame.module.css'
import { appById, appName } from './apps/index.ts'
import { HomeIndicator } from './home-indicator.tsx'
import { motionReduced, rectInOs } from './motion.ts'
import type { OsRect, OsState, OsWindow } from './os-state.ts'
import { startWindowDrag } from './window-drag.ts'

interface AppFrameProps {
  readonly state: OsState
  readonly entry: OsWindow
}

/** iOS's app launch: quick out of the icon, a long soft landing. */
const OPEN = { duration: 420, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' } as const
const CLOSE = { duration: 160, easing: 'ease-in', fill: 'forwards' } as const

/**
 * An app in its frame. The phone: full screen with no chrome, the home indicator at the
 * bottom. The desktop: a window with traffic lights (close, minimise, zoom), dragged by its
 * title bar, brought to the front by a press. Either zooms out of its launcher icon.
 */
export function AppFrame({ state, entry }: AppFrameProps) {
  const t = useT()
  const frame = useRef<HTMLElement>(null)
  // Where it opened from, as it was at mount: the zoom plays once.
  const origin = useRef(entry.origin)
  const { component: Content } = appById(entry.app)
  const name = t(appName(entry.app))

  useLayoutEffect(() => {
    if (frame.current && origin.current && !motionReduced()) {
      zoomFrom(frame.current, origin.current)
    }
  }, [])

  if (state.form === 'phone') {
    return (
      <section ref={frame} class={styles.screen} aria-label={name}>
        <div class={styles.content}>
          <Content os={state} />
        </div>
        <HomeIndicator
          onHome={() => {
            state.home()
          }}
        />
      </section>
    )
  }

  const close = (): void => {
    const element = frame.current
    if (!element || motionReduced()) {
      state.close(entry.app)
      return
    }
    const away = { opacity: 0, transform: 'translate(2%, 2%) scale(0.96)' }
    void element
      .animate([{}, away], CLOSE)
      .finished.then(() => {
        state.close(entry.app)
      })
      .catch(() => undefined)
  }
  const front = state.front.value === entry.app
  const position = entry.zoomed
    ? {}
    : { left: `calc(${String(entry.x)} * var(--pt))`, top: `calc(${String(entry.y)} * var(--pt))` }

  return (
    <section
      ref={frame}
      class={styles.window}
      aria-label={name}
      data-front={front || undefined}
      data-zoomed={entry.zoomed || undefined}
      data-minimised={entry.minimised || undefined}
      inert={entry.minimised}
      style={{ ...position, zIndex: entry.z }}
      onPointerDown={() => {
        state.focus(entry.app)
      }}
    >
      <div
        class={styles.titleBar}
        onPointerDown={(event) => {
          if (frame.current) startWindowDrag(event, state, entry, frame.current)
        }}
        onDblClick={() => {
          state.zoom(entry.app)
        }}
      >
        <div class={styles.lights}>
          <button
            type="button"
            class={[styles.light, styles.close].join(' ')}
            aria-label={t(text('os.window.close'))}
            onClick={close}
          >
            <X aria-hidden="true" />
          </button>
          <button
            type="button"
            class={[styles.light, styles.minimise].join(' ')}
            aria-label={t(text('os.window.minimise'))}
            onClick={() => {
              state.minimise(entry.app)
            }}
          >
            <Minus aria-hidden="true" />
          </button>
          <button
            type="button"
            class={[styles.light, styles.zoom].join(' ')}
            aria-label={t(text('os.window.zoom'))}
            onClick={() => {
              state.zoom(entry.app)
            }}
          >
            <Plus aria-hidden="true" />
          </button>
        </div>
        <span class={styles.title}>{name}</span>
      </div>
      <div class={styles.content}>
        <Content os={state} />
      </div>
    </section>
  )
}

/** Plays the frame growing out of `origin` (both relative to the OS's screen). */
function zoomFrom(element: HTMLElement, origin: OsRect): void {
  const to = rectInOs(element)
  if (!to || to.width === 0 || to.height === 0) return
  const dx = origin.x - to.x
  const dy = origin.y - to.y
  const scale = `scale(${String(origin.width / to.width)}, ${String(origin.height / to.height)})`
  element.animate(
    [
      { transform: `translate(${String(dx)}px, ${String(dy)}px) ${scale}`, opacity: 0 },
      { opacity: 1, offset: 0.3 },
      { transform: 'none', opacity: 1 },
    ],
    OPEN,
  )
}
